import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trophy, Eye, AlertTriangle, Users, MapPin, LifeBuoy, Download } from "lucide-react";
import api, { API_ROOT } from "../../lib/api";
import { StatCard, Label, TrendPill, scoreColor, IndustryCohort, InfoBadge } from "../../components/common";
import { TrendArea, Donut, RadialGauge } from "../../components/charts";
import FrontlineExperience from "../../components/FrontlineExperience";
import { OnboardingChecklist } from "../../components/Onboarding";
import { Button } from "../../components/ui/button";
import RatingsCollected7d from "../../components/RatingsCollected7d";
import ResponsesSummary from "../../components/ResponsesSummary";
import TriggerLeaderboard from "../../components/TriggerLeaderboard";
import PeriodFilter, { periodLabel } from "../../components/PeriodFilter";
import ChannelSplit from "../../components/ChannelSplit";
import IndexVsMarket from "../../components/IndexVsMarket";
import { feedbackHref } from "../../lib/feedbackLink";
import StoreKpiTile from "../../components/StoreKpiTile";

export default function CorporateDashboard() {
  const [d, setD] = useState(null);
  const [org, setOrg] = useState(null);
  const [err, setErr] = useState(null);
  const [health, setHealth] = useState(null);
  const [days, setDays] = useState("7");
  const bucket = (ssi) => (ssi >= 4 ? "Healthy" : ssi >= 3.5 ? "Watch" : "At-risk");
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/dashboard/corporate?days=${days}`).then((r) => setD(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Failed to load"));
  }, [days]);
  useEffect(() => {
    api.get("/organization").then((r) => setOrg(r.data.organization)).catch(() => {});
  }, []);

  if (err) return <div className="text-slate-500">{err}</div>;
  if (!d) return <div className="text-slate-400">Loading dashboard…</div>;

  const s = d.org_score;
  const rm = d.recovery_metrics;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-center gap-3">
          {org?.logo_url && <img src={org.logo_url} alt={org.name} className="h-12 w-12 rounded-lg border border-slate-200 bg-white object-contain p-1" data-testid="corp-dash-logo" />}
          <div>
            <Label>{org?.name || "Corporate Overview"}</Label>
            <h1 className="font-head text-2xl font-bold text-slate-900 sm:text-3xl">Organisation Service Performance</h1>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <PeriodFilter value={days} onChange={setDays} testId="corp-period" />
          <a href={`${API_ROOT}/reports/locations.csv`} data-testid="export-locations-csv">
            <Button variant="outline"><Download className="mr-2 h-4 w-4" /> Export locations</Button>
          </a>
        </div>
      </div>

      <OnboardingChecklist />

      <FrontlineExperience />

      {(() => {
        const healthy = d.ranking.filter((c) => c.score.ssi >= 4).length;
        const watch = d.ranking.filter((c) => c.score.ssi >= 3.5 && c.score.ssi < 4).length;
        const atRisk = d.ranking.filter((c) => c.score.ssi < 3.5).length;
        const resolved = Math.max(0, rm.total_cases - rm.open_cases);
        const tp = s.touchpoints || {};
        const driverData = [
          { name: "Welcomed", value: tp.welcomed || 0, color: "#4338CA" },
          { name: "Understood", value: tp.understood || 0, color: "#6366F1" },
          { name: "Explained", value: tp.explained || 0, color: "#F59E0B" },
          { name: "Resolved", value: tp.resolved || 0, color: "#10B981" },
        ];
        return (
          <div className="grid gap-6 sm:grid-cols-3">
            <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="gauge-service-index">
              <span className="flex items-center gap-1.5"><Label>Organisation Service Index</Label>
                <InfoBadge title="Organisation Service Index" testId="gauge-index-info"
                  text="Your whole group's service score out of 100, weighted by how many verified customer ratings sit behind it. It's the single number that sums up service quality across every location." /></span>
              <RadialGauge value={s.ssi_percent} suffix="" color={scoreColor(s.ssi)} label="/ 100" testId="radial-service-index" />
              <p className="text-center text-xs text-slate-400">{Math.round(s.confidence * 100)}% confidence · {s.verified_interactions.toLocaleString()} verified interactions</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="donut-location-health">
              <span className="flex items-center gap-1.5"><Label>Location Health</Label>
                <InfoBadge title="Location Health" testId="loc-health-info"
                  text="A breakdown of your branches by how they're scoring: Healthy (strong), Watch (okay but slipping) and At-risk (below target). It shows at a glance how much of your estate needs help." /></span>
              <Donut testId="loc-health-donut" centerValue={d.totals.locations} centerLabel="locations" onSlice={setHealth} activeName={health}
                data={[
                  { name: "Healthy", value: healthy, color: "#10B981" },
                  { name: "Watch", value: watch, color: "#F59E0B" },
                  { name: "At-risk", value: atRisk, color: "#EF4444" },
                ]} />
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="donut-recovery">
              <span className="flex items-center gap-1.5"><Label>Service Recovery</Label>
                <InfoBadge title="Service Recovery" testId="recovery-info"
                  text="Service recovery is the process of following up unhappy customers to put things right. This shows how many cases are still open versus resolved, plus how quickly your team responds." /></span>
              <Donut testId="recovery-donut" centerValue={rm.total_cases} centerLabel={`cases · ${periodLabel(d.days).toLowerCase()}`} onSlice={() => navigate("/app/recovery")}
                data={[
                  { name: "Open", value: rm.open_cases, color: "#EF4444" },
                  { name: "Resolved", value: resolved, color: "#10B981" },
                ]} />
              <p className="text-center text-xs text-slate-400">
                {rm.avg_response_hours != null ? `~${rm.avg_response_hours}h to respond` : "—"} · {rm.avg_resolution_hours != null ? `~${rm.avg_resolution_hours}h to resolve` : "—"}
              </p>
            </div>
          </div>
        );
      })()}

      <IndexVsMarket variant="market" title="How you compare against the market" />

      <div className="grid gap-4 md:grid-cols-3">
        <StatCard label="Verified Interactions" value={s.verified_interactions.toLocaleString()} sub={`Trust-weighted · ${periodLabel(d.days)}`} accent="#4338CA" testId="stat-interactions" onClick={() => navigate(feedbackHref({ days: d.days }))}
          help={{ title: "Verified Interactions", text: "The number of customer ratings that passed our anti-gaming checks. 'Trust-weighted' means fake or suspicious ratings are filtered out, so you can trust the count." }}><Users className="h-6 w-6 text-slate-300" /></StatCard>
        <StatCard label="Active Locations" value={d.totals.locations} sub={`${d.totals.employees} employees`} testId="stat-locations" onClick={() => navigate("/app/locations")}
          help={{ title: "Active Locations", text: "How many branches are live on Servantage, and the total number of frontline employees being measured across them." }}><MapPin className="h-6 w-6 text-slate-300" /></StatCard>
        <StatCard label="Open Recovery Cases" value={d.totals.open_recovery} sub={rm.avg_resolution_hours ? `~${rm.avg_resolution_hours}h avg resolution` : "—"} accent="#EF4444" testId="stat-open-recovery" onClick={() => navigate("/app/recovery")}
          help={{ title: "Open Recovery Cases", text: "Unhappy-customer cases that are still being worked on. The lower this is, the faster your team is closing the loop with dissatisfied customers." }}><LifeBuoy className="h-6 w-6 text-slate-300" /></StatCard>
      </div>

      <ChannelSplit channels={d.channels} days={d.days} testId="corp-channel-split" />

      <IndustryCohort you={s.ssi} industry={d.industry_index} label={d.industry} />

      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1.5"><Label>Service Trend · {d.trend_days} days</Label>
            <InfoBadge title="Service Trend" testId="trend-info"
              text="Your organisation's daily Service Index for the selected period (at least the last 7 days, so 'Today' still shows context). Use it to spot whether service is improving, holding steady, or dropping." /></span>
          <span className="text-xs text-slate-400">Index 0–100</span>
        </div>
        <div className="mt-3"><TrendArea data={d.org_trend} testId="org-trend-chart" /></div>
      </div>

      <RatingsCollected7d testId="corp-ratings-7d" />

      <StoreKpiTile testId="corp-store-kpi-tile" />

      <ResponsesSummary days={d.days} testId="corp-responses-summary" />
      <TriggerLeaderboard days={d.days} testId="corp-trigger-leaderboard" />

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <Panel title="Leaders" icon={Trophy} accent="#10B981" testId="panel-leaders"
          help={{ title: "Leaders", text: "Your top-performing locations — the branches with the highest service scores. Great places to learn best practices from and celebrate." }}
          rows={d.leaders} empty="No locations above threshold yet."
          render={(c) => <RankRow key={c.location.id} name={c.location.name} ssi={c.score.ssi} onClick={() => navigate(`/app/store?location=${c.location.id}`)} />} />
        <Panel title="Watchlist" icon={Eye} accent="#F59E0B" testId="panel-watchlist"
          help={{ title: "Watchlist", text: "Locations whose scores are trending downward. They're not in crisis yet, but they're worth keeping an eye on before things slip further." }}
          rows={d.watchlist} empty="No locations trending down."
          render={(c) => <RankRow key={c.location.id} name={c.location.name} ssi={c.score.ssi} trend={c.trend_pct} onClick={() => navigate(`/app/store?location=${c.location.id}`)} />} />
        <Panel title="Intervention Required" icon={AlertTriangle} accent="#EF4444" testId="panel-intervention"
          help={{ title: "Intervention Required", text: "Locations scoring below target that need hands-on management action now. These are your highest priorities for coaching and support." }}
          rows={d.intervention} empty="No locations below target."
          render={(c) => <RankRow key={c.location.id} name={c.location.name} ssi={c.score.ssi} onClick={() => navigate(`/app/store?location=${c.location.id}`)} />} />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <span className="flex items-center gap-1.5"><Label>Location Ranking</Label>
          <InfoBadge title="Location Ranking" testId="ranking-info"
            text="Every location ranked by Service Index, with its trend, number of ratings, biggest weak spot and open recovery cases. Click any row to drill into that branch." /></span>
          {health && <button onClick={() => setHealth(null)} data-testid="clear-health-filter" className="ml-2 rounded-full bg-indigo-50 px-3 py-1 text-xs font-bold text-indigo-600 hover:bg-indigo-100">{health} only ×</button>}
        <div className="mt-3 overflow-x-auto sv-scroll">
          <table className="w-full min-w-[640px] text-sm" data-testid="location-ranking-table">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-400">
                <th className="py-2">#</th><th>Location</th><th>Service Index</th><th>Trend</th><th>Interactions</th><th>Biggest gap</th><th>Open cases</th>
              </tr>
            </thead>
            <tbody>
              {d.ranking.filter((c) => !health || bucket(c.score.ssi) === health).map((c, i) => (
                <tr key={c.location.id} className="cursor-pointer border-b border-slate-100 hover:bg-slate-50"
                  onClick={() => navigate(`/app/store?location=${c.location.id}`)} data-testid={`rank-row-${c.location.id}`}>
                  <td className="py-3 font-bold text-slate-400">{i + 1}</td>
                  <td className="font-semibold text-slate-800">{c.location.name}</td>
                  <td><span className="sv-metric font-bold" style={{ color: scoreColor(c.score.ssi) }}>{c.score.ssi.toFixed(2)}</span>
                    {!c.score.meets_threshold && <span className="ml-2 text-[10px] uppercase text-amber-500">low sample</span>}</td>
                  <td><TrendPill value={c.trend_pct} /></td>
                  <td className="text-slate-500">{c.score.verified_interactions}</td>
                  <td className="text-slate-500">{c.gap?.gap_label || "—"}</td>
                  <td className="text-slate-500">{c.open_cases}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Panel({ title, icon: Icon, accent, rows, render, empty, help, testId }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={testId}>
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4" style={{ color: accent }} />
        <Label>{title}</Label>
        {help && <InfoBadge title={help.title} text={help.text} testId={`${testId}-info`} />}
      </div>
      <div className="mt-3 space-y-2">
        {(!rows || rows.length === 0) ? <p className="py-6 text-center text-sm text-slate-400">{empty}</p> : rows.map(render)}
      </div>
    </div>
  );
}

function RankRow({ name, ssi, trend, onClick }) {
  return (
    <button onClick={onClick} className="flex w-full items-center justify-between rounded-lg px-2 py-2 text-left hover:bg-slate-50">
      <span className="font-semibold text-slate-700">{name}</span>
      <span className="flex items-center gap-2">
        {trend != null && <TrendPill value={trend} />}
        <span className="sv-metric font-bold" style={{ color: scoreColor(ssi) }}>{ssi.toFixed(2)}</span>
      </span>
    </button>
  );
}
